import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Shield, ChevronLeft, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientProfile() {
  const { api, profile, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [healthPlans, setHealthPlans] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    health_plan_id: '',
    health_plan_card_number: '',
    health_plan_type: '',
    health_plan_holder_name: '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        health_plan_id: profile.health_plan_id || '',
        health_plan_card_number: profile.health_plan_card_number || '',
        health_plan_type: profile.health_plan_type || '',
        health_plan_holder_name: profile.health_plan_holder_name || '',
      });
    }
    fetchHealthPlans();
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchHealthPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/health-plans`);
      setHealthPlans(response.data);
    } catch (error) {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        health_plan_id: formData.health_plan_id === 'none' ? null : formData.health_plan_id
      };
      await api.put('/patient/profile', dataToSend);
      toast.success('Perfil atualizado com sucesso');
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-profile">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/paciente" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Meu Perfil</h1>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
            {/* Personal Info */}
            <div>
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Dados Pessoais
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nome Completo</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    data-testid="profile-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="profile-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>CPF</Label>
                  <Input value={profile?.cpf || ''} disabled className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>Data de Nascimento</Label>
                  <Input value={profile?.birth_date || ''} disabled className="bg-slate-50" />
                </div>
              </div>
            </div>

            {/* Health Plan */}
            <div className="border-t border-slate-100 pt-6">
              <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Convênio / Plano de Saúde
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="health_plan">Convênio</Label>
                  <Select 
                    value={formData.health_plan_id} 
                    onValueChange={(value) => setFormData({ ...formData, health_plan_id: value })}
                  >
                    <SelectTrigger data-testid="profile-health-plan">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {healthPlans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="card_number">Número da Carteira</Label>
                  <Input
                    id="card_number"
                    value={formData.health_plan_card_number}
                    onChange={(e) => setFormData({ ...formData, health_plan_card_number: e.target.value })}
                    placeholder="Número da carteira"
                    data-testid="profile-card-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plan_type">Tipo de Plano</Label>
                  <Input
                    id="plan_type"
                    value={formData.health_plan_type}
                    onChange={(e) => setFormData({ ...formData, health_plan_type: e.target.value })}
                    placeholder="Ex: Empresarial, Individual"
                    data-testid="profile-plan-type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="holder_name">Nome do Titular</Label>
                  <Input
                    id="holder_name"
                    value={formData.health_plan_holder_name}
                    onChange={(e) => setFormData({ ...formData, health_plan_holder_name: e.target.value })}
                    placeholder="Se dependente, nome do titular"
                    data-testid="profile-holder-name"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button type="submit" className="bg-primary" disabled={loading} data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
